import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  inject,
} from '@angular/core';

import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {
  HubspotService,
  HubspotSubmissionResult,
} from '../../core/_services/hubspot.service';

export type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

@Component({
  selector: 'app-email-signup',
  imports: [ReactiveFormsModule],
  templateUrl: './email-signup.component.html',
  styleUrl: './email-signup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailSignupComponent {
  private readonly fb = inject(FormBuilder);
  private readonly hubspotService = inject(HubspotService);

  /** HubSpot Portal ID */
  portalId = input.required<string>();

  /** HubSpot Form GUID */
  formId = input.required<string>();

  /** Placeholder text for the email input */
  placeholder = input<string>('SUBSCRIBE TO BITES');

  /** Button text */
  buttonText = input<string>('');

  /** Success message to display after submission */
  successMessage = input<string>('Thank you for subscribing!');

  /** Error message to display on submission failure */
  errorMessage = input<string>('Something went wrong. Please try again.');

  /** Emitted when form is successfully submitted */
  submitted = output<string>();

  /** Emitted when an error occurs */
  submitError = output<string>();

  protected readonly submitStatus = signal<SubmitStatus>('idle');

  protected readonly isSubmitting = computed(
    () => this.submitStatus() === 'submitting',
  );
  protected readonly isSuccess = computed(
    () => this.submitStatus() === 'success',
  );
  protected readonly isError = computed(() => this.submitStatus() === 'error');

  protected readonly emailForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  protected readonly emailControl = this.emailForm.controls.email;

  protected onSubmit(): void {
    if (this.emailForm.invalid || this.isSubmitting()) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const email = this.emailControl.value;
    this.submitStatus.set('submitting');

    this.hubspotService
      .submitForm(this.portalId(), this.formId(), email)
      .subscribe({
        next: (result: HubspotSubmissionResult) => {
          if (result.success) {
            this.submitStatus.set('success');
            this.submitted.emit(email);
            this.emailForm.reset();
          } else {
            this.submitStatus.set('error');
            this.submitError.emit(result.message || this.errorMessage());
          }
        },
        error: err => {
          this.submitStatus.set('error');
          this.submitError.emit(err.message || this.errorMessage());
        },
      });
  }

  protected resetForm(): void {
    this.submitStatus.set('idle');
    this.emailForm.reset();
  }
}
